// ── BCHNostr — Creator video channel ──────────────────────────────
//
// Every creator has a channel automatically, because a channel IS their
// Nostr profile: same key, same name, same avatar, same follower graph.
// There is no separate video account to create, claim, or migrate.
//
//   BCHNostr profile → Videos → Creator channel
//
// "Subscribers" are plain kind-3 followers, so following someone here is
// the same act as following them anywhere else in the app. Tips,
// memberships and goals all reuse the existing creator-economy rails.
//
// Podcast compatibility: if the creator also has a podcast, this page links
// to it rather than reimplementing it. One identity, two media surfaces.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  ArrowLeft, Video, Users, Plus, ListVideo, Clock, Flame, Coins, Eye,
  MousePointerClick, BarChart3, Mic2, Rocket, Pencil, Trash2, Loader2,
  ExternalLink, Globe as GlobeIcon, Star, BadgeCheck,
} from 'lucide-react';
import { useAppContext } from '../hooks/useAppContext';
import { useNostrProfile } from '../hooks/useNostrQuery';
import { useFollowers } from '../hooks/useFollows';
import { useBCHAddress } from '../hooks/useBCHAddress';
import { useBCHPrice } from '../hooks/useBCHPrice';
import { useBusinessProfile } from '../hooks/useBusinessProfile';
import { useBCHPodcast } from '../hooks/usePodcast';
import {
  useChannelVideos, useVideoEngagement, useVideoPlaylists, useVideoBoosts,
  useVideoPublish,
} from '../hooks/useVideo';
import VideoCard, { VideoCardSkeleton } from '../components/VideoCard';
import AddVideoModal from '../components/AddVideoModal';
import VerifyChannelModal from '../components/VerifyChannelModal';
import { useCreatorVerifications } from '../hooks/useCreatorVerification';
import { displayChannel } from '../lib/creatorVerify';
import FollowButton from '../components/FollowButton';
import BCHTipButton from '../components/BCHTipButton';
import UserBadges from '../components/UserBadges';
import { CreatorSupportPanel } from './CreatorSupportPage';
import { VideoEmptyState } from './VideosPage';
import { podcastPath } from '../lib/podcast';
import { professionalTypeLabel } from '../lib/business';
import {
  videoCoordinate, playlistPath, videoViewCount, summarizeChannelStats,
  formatCount, videoCategoryLabel, EMPTY_ENGAGEMENT, recordChannelView,
  type BCHVideo, type VideoPlaylist,
} from '../lib/video';

type Tab = 'videos' | 'popular' | 'playlists' | 'support';

const TABS: Array<{ id: Tab; label: string; icon: React.ElementType }> = [
  { id: 'videos',    label: 'Videos',    icon: Clock },
  { id: 'popular',   label: 'Popular',   icon: Flame },
  { id: 'playlists', label: 'Playlists', icon: ListVideo },
  { id: 'support',   label: 'Support',   icon: Coins },
];

export default function VideoChannelPage() {
  const { pubkey: rawPubkey } = useParams<{ pubkey: string }>();
  const pubkey = useMemo(
    () => (rawPubkey && /^[0-9a-f]{64}$/i.test(rawPubkey) ? rawPubkey.toLowerCase() : undefined),
    [rawPubkey],
  );

  const { user } = useAppContext();
  const { profile } = useNostrProfile(pubkey);
  const { videos, loading, refetch } = useChannelVideos(pubkey);
  const engagement = useVideoEngagement(videos);
  const { playlists, refetch: refetchPlaylists } = useVideoPlaylists(pubkey);
  const { byVideo: boostsByVideo } = useVideoBoosts();
  const { followerCount, loading: followersLoading } = useFollowers(pubkey);
  const { address: creatorAddress } = useBCHAddress(pubkey);
  const { price: bchPrice } = useBCHPrice();
  const { business } = useBusinessProfile(pubkey);
  const { podcast } = useBCHPodcast(pubkey);
  const { verifications, refetch: refetchVerifications } = useCreatorVerifications(pubkey);

  const [tab, setTab] = useState<Tab>('videos');
  const [addOpen, setAddOpen] = useState(false);
  const [verifyOpen, setVerifyOpen] = useState(false);

  const isOwner = !!user && !!pubkey && user.pubkey === pubkey;

  useEffect(() => {
    if (pubkey) recordChannelView(pubkey);
  }, [pubkey]);

  /**
   * "Popular" ranks by real engagement — how many DIFFERENT people tipped,
   * replied or reacted. Not by a view count, because BCHNostr has no
   * trustworthy one for media it doesn't serve.
   */
  const popular = useMemo(() => {
    return [...videos].sort((a, b) => {
      const ea = engagement.get(videoCoordinate(a.pubkey, a.id)) || EMPTY_ENGAGEMENT;
      const eb = engagement.get(videoCoordinate(b.pubkey, b.id)) || EMPTY_ENGAGEMENT;
      const score = (e: typeof ea) =>
        e.uniqueTippers * 8 + e.comments * 4 + e.reactions + Math.log10(1 + e.tipSats) * 6;
      const diff = score(eb) - score(ea);
      return diff !== 0 ? diff : b.publishedAt - a.publishedAt;
    });
  }, [videos, engagement]);

  if (!pubkey) {
    return <VideoEmptyState title="Channel not found" body="That channel link isn’t valid." />;
  }

  const name = profile?.display_name || profile?.name || pubkey.slice(0, 12);
  const about = typeof profile?.about === 'string' ? profile.about : '';
  const tipAddress = creatorAddress || '';
  const list = tab === 'popular' ? popular : videos;

  return (
    <div className="pb-8">
      {/* ── Header ── */}
      <div className="sticky top-0 z-10 bg-background/80 backdrop-blur-md border-b border-border px-4 py-3 flex items-center gap-2">
        <Link to="/videos" className="p-1.5 -ml-1.5 rounded-lg hover:bg-muted text-muted-foreground">
          <ArrowLeft className="w-4 h-4" />
        </Link>
        <h1 className="text-base font-bold truncate flex-1 flex items-center gap-1.5">
          <Video className="w-4 h-4 text-primary shrink-0" /> {name}
        </h1>
        {isOwner && (
          <button
            onClick={() => setAddOpen(true)}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-primary text-primary-foreground text-xs font-semibold hover:bg-primary/90"
          >
            <Plus className="w-3.5 h-3.5" /> Add
          </button>
        )}
      </div>

      {/* ── Banner ── */}
      {typeof profile?.banner === 'string' && profile.banner && (
        <div className="h-24 sm:h-36 w-full overflow-hidden bg-muted">
          <img src={profile.banner} alt="" className="w-full h-full object-cover" />
        </div>
      )}

      {/* ── Channel identity ── */}
      <div className="px-4 pt-4">
        <div className="flex items-start gap-3.5">
          {profile?.picture ? (
            <img
              src={profile.picture}
              alt=""
              className="w-20 h-20 sm:w-24 sm:h-24 rounded-full object-cover border-2 border-background shrink-0 -mt-8 sm:-mt-10 bg-muted"
            />
          ) : (
            <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full bg-primary/10 border-2 border-background flex items-center justify-center shrink-0 -mt-8 sm:-mt-10">
              <Video className="w-8 h-8 text-primary/50" />
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-1.5 flex-wrap">
              <h2 className="text-lg font-extrabold leading-tight truncate">{name}</h2>
              <UserBadges pubkey={pubkey} size="sm" />
            </div>

            <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1 text-[11px] text-muted-foreground">
              <span className="flex items-center gap-1" title="Nostr followers — this channel has no separate subscriber list">
                <Users className="w-2.5 h-2.5" />
                {followersLoading && followerCount === 0
                  ? '…'
                  : `${formatCount(followerCount)} follower${followerCount === 1 ? '' : 's'}`}
              </span>
              <span className="flex items-center gap-1">
                <Video className="w-2.5 h-2.5" />
                {videos.length} video{videos.length === 1 ? '' : 's'}
              </span>
              {business && (
                <span className="px-1.5 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400 font-semibold">
                  {professionalTypeLabel(business.proType)}
                </span>
              )}
              {verifications.length > 0 && (
                <span
                  className="flex items-center gap-1 px-1.5 py-0.5 rounded-full bg-primary/10 text-primary font-semibold"
                  title={`Verified creator: ${verifications.map(v => `${displayChannel(v.channel)} (${v.platform})`).join(', ')}`}
                >
                  <BadgeCheck className="w-2.5 h-2.5" /> Verified creator
                </span>
              )}
            </div>
            {isOwner && (
              <button
                onClick={() => setVerifyOpen(true)}
                className="mt-2 flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border border-primary/40 text-primary text-[11px] font-semibold hover:bg-primary/5"
              >
                <BadgeCheck className="w-3.5 h-3.5" />
                {verifications.length > 0 ? 'Verify another channel' : 'Verify your channel'}
              </button>
            )}

            {/* Their Nostr identity remains the source of truth. */}
            <Link
              to={`/profile/${pubkey}`}
              className="inline-flex items-center gap-1 mt-1.5 text-[11px] text-primary hover:underline"
            >
              View Nostr profile <ExternalLink className="w-2.5 h-2.5" />
            </Link>
          </div>
        </div>

        {about && (
          <p className="text-xs text-muted-foreground whitespace-pre-wrap mt-3 leading-relaxed line-clamp-4">
            {about}
          </p>
        )}

        {/* ── Actions ── */}
        <div className="flex flex-wrap items-center gap-2 mt-3.5">
          <FollowButton pubkey={pubkey} size="md" />
          {tipAddress && !isOwner && (
            <div className="no-navigate">
              <BCHTipButton recipientPubkey={pubkey} recipientProfile={profile} />
            </div>
          )}
          <button
            onClick={() => setTab('support')}
            className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40"
          >
            <Star className="w-3.5 h-3.5" /> Membership & goals
          </button>
          {typeof profile?.website === 'string' && profile.website && (
            <a
              href={profile.website.startsWith('http') ? profile.website : `https://${profile.website}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1.5 px-3 py-2 rounded-full border border-border text-xs font-semibold text-muted-foreground hover:text-primary hover:border-primary/40"
            >
              <GlobeIcon className="w-3.5 h-3.5" /> Website
            </a>
          )}
        </div>

        {/* ── Podcast cross-link ──
            One identity, two media surfaces. Videos and podcasts coexist on
            the same profile; neither reimplements the other. */}
        {podcast && (
          <Link
            to={podcastPath(pubkey)}
            className="flex items-center gap-2.5 mt-3 rounded-xl border border-border bg-card px-3 py-2.5 hover:border-primary/40 transition-colors group"
          >
            <div className="p-2 rounded-lg bg-primary/10 shrink-0">
              <Mic2 className="w-4 h-4 text-primary" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-xs font-bold group-hover:text-primary transition-colors">
                🎙 Also has a podcast
              </p>
              <p className="text-[11px] text-muted-foreground truncate">{podcast.name}</p>
            </div>
            <ExternalLink className="w-3.5 h-3.5 text-muted-foreground shrink-0" />
          </Link>
        )}
      </div>

      {/* ── Owner analytics ── */}
      {isOwner && <ChannelAnalytics pubkey={pubkey} videos={videos} followerCount={followerCount} />}

      {/* ── Tabs ── */}
      <div className="flex border-b border-border sticky top-[3.25rem] z-[9] bg-background/90 backdrop-blur-md mt-4">
        {TABS.map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`flex-1 flex items-center justify-center gap-1.5 py-3 text-xs font-semibold border-b-2 transition-colors ${
              tab === t.id
                ? 'border-primary text-primary'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            <t.icon className="w-3.5 h-3.5" /> {t.label}
          </button>
        ))}
      </div>

      {/* ── Body ── */}
      {tab === 'support' ? (
        <div className="p-4">
          {/* Reuses the existing creator economy wholesale — membership,
              goals, and crowdfunding. No separate video payment system. */}
          <CreatorSupportPanel pubkey={pubkey} bchPrice={bchPrice} />
        </div>
      ) : tab === 'playlists' ? (
        <PlaylistsTab
          pubkey={pubkey}
          playlists={playlists}
          isOwner={isOwner}
          onChanged={refetchPlaylists}
        />
      ) : loading && videos.length === 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5 p-4">
          {Array.from({ length: 3 }).map((_, i) => <VideoCardSkeleton key={i} />)}
        </div>
      ) : list.length === 0 ? (
        <div className="text-center py-16 px-6">
          <Video className="w-12 h-12 mx-auto text-muted-foreground/25 mb-3" />
          <p className="text-sm font-semibold">
            {isOwner ? 'You haven’t added any videos yet' : 'No videos on this channel yet'}
          </p>
          {isOwner && (
            <>
              <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">
                Paste a link to a video you've already published anywhere — YouTube,
                Odysee, PeerTube, or your own server. BCHNostr adds the audience, the
                discussion and the BCH.
              </p>
              <button
                onClick={() => setAddOpen(true)}
                className="mt-4 flex items-center gap-1.5 px-4 py-2 rounded-lg bg-primary text-primary-foreground text-xs font-semibold mx-auto hover:bg-primary/90"
              >
                <Plus className="w-3.5 h-3.5" /> Add your first video
              </button>
            </>
          )}
        </div>
      ) : (
        <>
          {tab === 'popular' && (
            <p className="px-4 py-2 text-[10px] text-muted-foreground border-b border-border">
              Ranked by how many different people tipped, replied or reacted — not by view
              count, which BCHNostr can't verify for video it doesn't host.
            </p>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-5 p-4">
            {list.map(video => (
              <VideoCard
                key={video.id}
                video={video}
                engagement={engagement.get(videoCoordinate(video.pubkey, video.id))}
                boost={boostsByVideo.get(`${video.pubkey}:${video.id}`)}
                localViews={videoViewCount(video.pubkey, video.id)}
              />
            ))}
          </div>
        </>
      )}

      {addOpen && (
        <AddVideoModal onClose={() => setAddOpen(false)} onPublished={() => refetch()} />
      )}

      {verifyOpen && (
        <VerifyChannelModal
          onClose={() => setVerifyOpen(false)}
          onVerified={() => refetchVerifications()}
        />
      )}
    </div>
  );
}

// ── Playlists tab ─────────────────────────────────────────────────

function PlaylistsTab({
  pubkey, playlists, isOwner, onChanged,
}: {
  pubkey: string;
  playlists: VideoPlaylist[];
  isOwner: boolean;
  onChanged: () => void;
}) {
  const { removePlaylist, savePlaylist, publishing } = useVideoPublish();
  const [editing, setEditing] = useState<string | null>(null);
  const [title, setTitle] = useState('');

  const handleRename = async (playlist: VideoPlaylist) => {
    const next = title.trim();
    if (!next) return;
    try {
      await savePlaylist(playlist.id, {
        title: next,
        description: playlist.description,
        image: playlist.image,
        videoRefs: playlist.videoRefs,
      });
      setEditing(null);
      onChanged();
    } catch {}
  };

  if (playlists.length === 0) {
    return (
      <div className="text-center py-16 px-6">
        <ListVideo className="w-12 h-12 mx-auto text-muted-foreground/25 mb-3" />
        <p className="text-sm font-semibold">No playlists yet</p>
        <p className="text-xs text-muted-foreground mt-1 max-w-sm mx-auto leading-relaxed">
          {isOwner
            ? 'Open any video and use Save to group it into a playlist — “BCH 101”, “Merchant Adoption”, whatever you like. Playlists reference videos; they never copy them.'
            : 'This creator hasn’t made any playlists.'}
        </p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 p-4">
      {playlists.map(pl => (
        <div key={pl.id} className="group">
          <Link to={playlistPath(pubkey, pl.id)}>
            <div className="relative aspect-video rounded-xl overflow-hidden bg-muted border border-border group-hover:border-primary/50 transition-colors">
              {pl.image ? (
                <img src={pl.image} alt="" loading="lazy" className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-primary/10 to-emerald-500/5">
                  <ListVideo className="w-7 h-7 text-primary/40" />
                </div>
              )}
              <span className="absolute bottom-1.5 right-1.5 px-1.5 py-0.5 rounded bg-black/80 text-white text-[10px] font-semibold">
                {pl.videoRefs.length} videos
              </span>
            </div>
          </Link>

          {editing === pl.id ? (
            <div className="flex gap-1 mt-1.5">
              <input
                value={title}
                onChange={e => setTitle(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter') handleRename(pl); }}
                maxLength={200}
                autoFocus
                className="flex-1 min-w-0 bg-muted rounded-lg px-2 py-1 text-[11px] focus:outline-none focus:ring-1 focus:ring-primary"
              />
              <button
                onClick={() => handleRename(pl)}
                disabled={publishing}
                className="px-2 py-1 rounded-lg bg-primary text-primary-foreground text-[10px] font-semibold disabled:opacity-50"
              >
                {publishing ? <Loader2 className="w-2.5 h-2.5 animate-spin" /> : 'Save'}
              </button>
            </div>
          ) : (
            <div className="flex items-start gap-1 mt-1.5">
              <Link to={playlistPath(pubkey, pl.id)} className="min-w-0 flex-1">
                <p className="text-xs font-semibold line-clamp-2 leading-tight group-hover:text-primary transition-colors">
                  {pl.title}
                </p>
              </Link>
              {isOwner && (
                <div className="flex items-center shrink-0 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => { setEditing(pl.id); setTitle(pl.title); }}
                    className="p-1 rounded text-muted-foreground hover:text-primary"
                    title="Rename playlist"
                  >
                    <Pencil className="w-2.5 h-2.5" />
                  </button>
                  <button
                    onClick={async () => { await removePlaylist(pl.id); onChanged(); }}
                    className="p-1 rounded text-muted-foreground hover:text-red-500"
                    title="Delete playlist"
                  >
                    <Trash2 className="w-2.5 h-2.5" />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      ))}
    </div>
  );
}

// ── Owner analytics ───────────────────────────────────────────────
//
// Honest by construction. BCHNostr can see its own page views and Play
// clicks, and it can count Nostr engagement. It cannot see plays or watch
// time on the provider, and it says so instead of guessing.

function ChannelAnalytics({
  pubkey, videos, followerCount,
}: {
  pubkey: string;
  videos: BCHVideo[];
  followerCount: number;
}) {
  const [open, setOpen] = useState(false);
  const engagement = useVideoEngagement(videos);
  const { boosts } = useVideoBoosts();

  const stats = useMemo(() => summarizeChannelStats(pubkey), [pubkey, open]);

  const totals = useMemo(() => {
    let tippers = 0, tipSats = 0, comments = 0, reactions = 0;
    for (const v of videos) {
      const e = engagement.get(videoCoordinate(v.pubkey, v.id));
      if (!e) continue;
      tippers += e.uniqueTippers;
      tipSats += e.tipSats;
      comments += e.comments;
      reactions += e.reactions;
    }
    return { tippers, tipSats, comments, reactions };
  }, [videos, engagement]);

  const boostCount = boosts.filter(b => b.videoPubkey === pubkey).length;

  return (
    <div className="mx-4 mt-4 rounded-xl border border-border bg-card overflow-hidden">
      <button
        onClick={() => setOpen(v => !v)}
        className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-muted/40 transition-colors"
      >
        <BarChart3 className="w-4 h-4 text-primary shrink-0" />
        <span className="text-xs font-bold flex-1">Channel analytics</span>
        <span className="text-[10px] text-muted-foreground">{open ? 'Hide' : 'Show'}</span>
      </button>

      {open && (
        <div className="px-3 pb-3 pt-3 space-y-2 border-t border-border">
          <div className="grid grid-cols-2 gap-2">
            <Stat icon={Eye} label="Channel page views" value={stats.channelViews} />
            <Stat icon={Eye} label="Video page views" value={stats.videoViews} />
            <Stat icon={MousePointerClick} label="Play clicks" value={stats.playClicks} />
            <Stat icon={Users} label="Followers" value={followerCount} />
            <Stat icon={Coins} label="Tips received" value={totals.tippers} />
            <Stat icon={Rocket} label="Boosts on your videos" value={boostCount} />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <Stat icon={Video} label="Comments" value={totals.comments} />
            <Stat icon={Flame} label="Reactions" value={totals.reactions} />
          </div>

          <div className="rounded-lg bg-muted/50 border border-border p-2.5 space-y-1.5">
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              <strong className="text-foreground">Page views</strong> and{' '}
              <strong className="text-foreground">play clicks</strong> count actions inside
              BCHNostr, recorded in this browser only. They are{' '}
              <strong className="text-foreground">not external views</strong> and{' '}
              <strong className="text-foreground">not watch time</strong> — your videos are
              served by their own hosts, so only those hosts can measure that.
            </p>
            <p className="text-[10px] text-muted-foreground leading-relaxed">
              Followers, comments, reactions and tips are read from Nostr relays and are
              therefore approximate.
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function Stat({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: number }) {
  return (
    <div className="rounded-lg border border-border bg-background p-2.5">
      <div className="flex items-center gap-1.5">
        <Icon className="w-3 h-3 text-primary shrink-0" />
        <span className="text-[10px] text-muted-foreground leading-tight">{label}</span>
      </div>
      <p className="text-lg font-bold tabular-nums mt-0.5">{formatCount(value)}</p>
    </div>
  );
}

/** Re-exported so other video pages can label a category consistently. */
export { videoCategoryLabel };
